import { useState, useCallback } from "react"
import { X, Check } from "lucide-react"
import { createPortal } from "react-dom"
import { useQueryClient } from "@tanstack/react-query"
import { useModalOpen } from "../lib/useModalOpen"
import { AnalogClock } from "./AnalogClock"
import { GLITCH_PALETTE } from "../lib/featureColors"
import { DEFAULT_ANALOG_CLOCK_SECONDS_COLOR, buildBrandingRowId, fetchBrandingRowForScope } from "../lib/useBranding"
import { supabase } from "../lib/supabase"
import { isDemoSite } from "../lib/isDemoSite"
import type { ScopeType } from "../lib/useBranding"

type TickMarks = 0 | 4 | 8 | 12

const TICK_OPTIONS: { value: TickMarks; label: string; sublabel: string }[] = [
  { value: 0,  label: "None",  sublabel: "clean" },
  { value: 4,  label: "4",     sublabel: "quarters" },
  { value: 8,  label: "8",     sublabel: "eighths" },
  { value: 12, label: "12",    sublabel: "all" },
]

// ---------------------------------------------------------------------------
// Color swatch — Trace-style
// ---------------------------------------------------------------------------

function ColorSwatch({
  hex,
  label,
  active,
  onClick,
}: {
  hex: string
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={`relative w-7 h-7 rounded-lg border-2 transition-all duration-150 ${
        active
          ? "scale-110 shadow-[0_0_10px_2px_var(--glow)]"
          : "border-transparent hover:border-white/30 hover:scale-105"
      }`}
      style={{
        backgroundColor: hex + "22",
        borderColor: active ? hex + "bb" : undefined,
        // @ts-expect-error css custom property
        "--glow": hex + "55",
      }}
    >
      <span
        className="absolute inset-1 rounded-md"
        style={{ backgroundColor: hex, opacity: active ? 0.95 : 0.65 }}
      />
    </button>
  )
}

// ---------------------------------------------------------------------------
// Toggle
// ---------------------------------------------------------------------------

function Toggle({
  checked,
  label,
  onLabel,
  offLabel,
  onChange,
}: {
  checked: boolean
  label: string
  onLabel: string
  offLabel: string
  onChange: (v: boolean) => void
}) {
  return (
    <div className="space-y-2">
      <p className="font-mono text-[9px] font-bold uppercase tracking-widest text-white/30">{label}</p>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`inline-flex items-center gap-2.5 px-4 py-2.5 rounded-xl border transition-colors duration-200 ${
          checked
            ? "bg-violet-500/20 border-violet-400/30 text-violet-300"
            : "bg-white/[0.04] border-white/10 text-white/40"
        }`}
      >
        <span
          className={`w-9 h-5 rounded-full border flex items-center transition-all duration-200 ${
            checked ? "bg-violet-500 border-violet-400 justify-end" : "bg-white/10 border-white/20 justify-start"
          }`}
        >
          <span className="w-3.5 h-3.5 rounded-full bg-white shadow-sm mx-0.5" />
        </span>
        <span className="font-mono text-[12px] font-bold">{checked ? onLabel : offLabel}</span>
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

export interface ClockSettingsModalProps {
  /** Scope to write to. Defaults to "platform". */
  scope?: ScopeType
  scopeId?: string | null
  initialSecondsColor?: string | null
  initialOutlineColor?: string | null
  initialFluidSeconds?: boolean
  initialShowDigitalTime?: boolean
  initialTickMarks?: TickMarks
  onSave?: (values: {
    secondsColor: string
    outlineColor: string
    fluidSeconds: boolean
    showDigitalTime: boolean
    tickMarks: TickMarks
  }) => void
  onClose: () => void
}

// A selection of outline colors — dim palette
const OUTLINE_PRESETS = [
  { hex: "rgba(120,113,108,0.45)", label: "Stone (default)" },
  { hex: "#2dd4bf", label: "Teal" },
  { hex: "#a78bfa", label: "Violet" },
  { hex: "#6675c8", label: "Luster" },
  { hex: "#6ee7b7", label: "Emerald" },
  { hex: "#fbbf24", label: "Amber" },
  { hex: "#fb7185", label: "Rose" },
  { hex: "rgba(255,255,255,0.15)", label: "Dim white" },
]

const DEFAULT_OUTLINE = "rgba(120,113,108,0.45)"

export function ClockSettingsModal({
  scope: scopeProp = "platform",
  scopeId: scopeIdProp = null,
  initialSecondsColor,
  initialOutlineColor,
  initialFluidSeconds = false,
  initialShowDigitalTime = false,
  initialTickMarks = 12,
  onSave,
  onClose,
}: ClockSettingsModalProps) {
  useModalOpen(true)

  const queryClient = useQueryClient()
  const [secondsColor, setSecondsColor] = useState(initialSecondsColor?.trim() || DEFAULT_ANALOG_CLOCK_SECONDS_COLOR)
  const [outlineColor, setOutlineColor] = useState(initialOutlineColor?.trim() || DEFAULT_OUTLINE)
  const [fluidSeconds, setFluidSeconds] = useState(initialFluidSeconds)
  const [showDigitalTime, setShowDigitalTime] = useState(initialShowDigitalTime)
  const [tickMarks, setTickMarks] = useState<TickMarks>(initialTickMarks)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = useCallback(async () => {
    setSaving(true)
    setError(null)
    try {
      if (!isDemoSite()) {
        const rowId = buildBrandingRowId(scopeProp, scopeIdProp)
        const existing = await fetchBrandingRowForScope(scopeProp, scopeIdProp)
        const payload = {
          id: rowId,
          scope_type: scopeProp,
          scope_id: scopeIdProp,
          logo_url: existing?.logo_url ?? null,
          dashboard_title: existing?.dashboard_title ?? null,
          powered_by_url: existing?.powered_by_url ?? null,
          docs_url: existing?.docs_url ?? null,
          banner_url: existing?.banner_url ?? null,
          strip_text: existing?.strip_text ?? null,
          strip_text_mobile: existing?.strip_text_mobile ?? null,
          strip_button_text: existing?.strip_button_text ?? null,
          strip_button_url: existing?.strip_button_url ?? null,
          strip_button_color: existing?.strip_button_color ?? null,
          strip_button_hover_color: existing?.strip_button_hover_color ?? null,
          strip_icon: existing?.strip_icon ?? null,
          strip_icon_color: existing?.strip_icon_color ?? null,
          strip_active_until: existing?.strip_active_until ?? null,
          analog_clock_seconds_color: secondsColor || null,
          analog_clock_outline_color: outlineColor || null,
          analog_clock_fluid_seconds: fluidSeconds,
          analog_clock_show_digital_time: showDigitalTime,
          analog_clock_tick_marks: tickMarks,
          dashboard_hero_label: existing?.dashboard_hero_label ?? null,
          dashboard_hero_heading: existing?.dashboard_hero_heading ?? null,
          status_pill_status: existing?.status_pill_status ?? null,
          status_pill_label: existing?.status_pill_label ?? null,
          status_pill_tooltip: existing?.status_pill_tooltip ?? null,
          status_pill_link_url: existing?.status_pill_link_url ?? null,
          glitch_effects_enabled: (existing as unknown as Record<string, unknown>)?.glitch_effects_enabled !== false,
          terminal_trace_enabled: (existing as unknown as Record<string, unknown>)?.terminal_trace_enabled !== false,
          updated_at: new Date().toISOString(),
        }
        const { error: dbError } = await supabase.from("portal_branding").upsert(payload, {
          onConflict: "id",
          ignoreDuplicates: false,
        })
        if (dbError) {
          const raw = dbError.message?.toLowerCase() ?? ""
          const isRls =
            raw.includes("row-level security") || raw.includes("security policy") ||
            raw.includes("violates") || raw.includes("permission denied")
          setError(isRls
            ? "You don't have permission to change this."
            : "Something went wrong. Please try again.")
          return
        }
        await queryClient.refetchQueries({ queryKey: ["portal-branding"], type: "active" })
        window.dispatchEvent(new CustomEvent("portal-branding-updated"))
      }

      onSave?.({ secondsColor, outlineColor, fluidSeconds, showDigitalTime, tickMarks })
      setSaved(true)
      setTimeout(() => { setSaved(false); onClose() }, 900)
    } catch {
      setError("Something went wrong. Please try again.")
    } finally {
      setSaving(false)
    }
  }, [secondsColor, outlineColor, fluidSeconds, showDigitalTime, tickMarks, onSave, onClose, queryClient, scopeProp, scopeIdProp])

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className="relative w-full max-w-sm rounded-2xl bg-black/80 backdrop-blur-2xl border border-white/[0.08] shadow-[0_32px_80px_-16px_rgba(0,0,0,0.7)] overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Clock settings"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            {/* Live preview */}
            <div
              className="w-8 h-8 rounded-xl overflow-hidden border border-white/[0.12] bg-black/60 flex items-center justify-center shrink-0"
              style={{ "--tw-ring-color": `color-mix(in srgb, ${secondsColor} 42%, transparent)` } as React.CSSProperties}
            >
              <AnalogClock
                size={28}
                secondsHandColor={secondsColor}
                outlineColor={outlineColor}
                fluidSeconds={fluidSeconds}
                tickMarks={tickMarks}
              />
            </div>
            <span className="font-mono text-[11px] font-bold uppercase tracking-widest text-white/70">
              Clock
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-white/30 hover:text-white/70 hover:bg-white/[0.06] transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Seconds hand color */}
          <div className="space-y-2.5">
            <p className="font-mono text-[9px] font-bold uppercase tracking-widest text-white/30">Seconds hand</p>
            <div className="flex items-center gap-2 flex-wrap">
              {GLITCH_PALETTE.map((c) => (
                <ColorSwatch
                  key={c.name}
                  hex={c.hex}
                  label={c.label}
                  active={secondsColor === c.hex}
                  onClick={() => setSecondsColor(c.hex)}
                />
              ))}
              {/* Custom color input */}
              <label className="relative w-7 h-7 rounded-lg border-2 border-white/20 overflow-hidden cursor-pointer hover:border-white/40 transition-colors" title="Custom color">
                <input
                  type="color"
                  value={secondsColor || DEFAULT_ANALOG_CLOCK_SECONDS_COLOR}
                  onChange={(e) => setSecondsColor(e.target.value)}
                  className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                />
                <span className="absolute inset-1 rounded-md" style={{ backgroundColor: secondsColor || DEFAULT_ANALOG_CLOCK_SECONDS_COLOR }} />
              </label>
            </div>
          </div>

          {/* Dial outline color */}
          <div className="space-y-2.5">
            <p className="font-mono text-[9px] font-bold uppercase tracking-widest text-white/30">Dial outline</p>
            <div className="flex items-center gap-2 flex-wrap">
              {OUTLINE_PRESETS.map((c) => (
                <ColorSwatch
                  key={c.label}
                  hex={c.hex}
                  label={c.label}
                  active={outlineColor === c.hex}
                  onClick={() => setOutlineColor(c.hex)}
                />
              ))}
            </div>
          </div>

          {/* Tick marks */}
          <div className="space-y-2.5">
            <p className="font-mono text-[9px] font-bold uppercase tracking-widest text-white/30">Tick marks</p>
            <div className="grid grid-cols-4 gap-2">
              {TICK_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTickMarks(opt.value)}
                  className={`flex flex-col items-center gap-1.5 px-2 py-2.5 rounded-xl border transition-all duration-150 ${
                    tickMarks === opt.value
                      ? "bg-luster-500/20 border-luster-400/40 text-luster-300"
                      : "bg-white/[0.03] border-white/[0.08] text-white/40 hover:border-white/20 hover:text-white/60"
                  }`}
                >
                  <svg width="28" height="28" viewBox="0 0 48 48" className="shrink-0">
                    <circle cx="24" cy="24" r="22" fill="#1c1917" stroke={tickMarks === opt.value ? "rgba(102,117,200,0.6)" : "rgba(120,113,108,0.35)"} strokeWidth="1" />
                    {(opt.value === 0 ? [] : opt.value === 4 ? [0,90,180,270] : opt.value === 8 ? [0,45,90,135,180,225,270,315] : Array.from({length:12},(_,i)=>i*30)).map((deg) => {
                      const isMajor = deg % 90 === 0
                      return (
                        <line
                          key={deg}
                          x1="24" y1={isMajor ? 4 : 5}
                          x2="24" y2="8"
                          stroke="rgba(255,255,255,0.65)"
                          strokeWidth={isMajor ? 1.2 : 0.8}
                          strokeLinecap="round"
                          transform={`rotate(${deg} 24 24)`}
                        />
                      )
                    })}
                    <circle cx="24" cy="24" r="1.5" fill="white" fillOpacity="0.5" />
                  </svg>
                  <span className="font-mono text-[9px] font-bold uppercase tracking-wider">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-white/[0.06]" />

          {/* Toggles */}
          <div className="grid grid-cols-2 gap-4">
            <Toggle
              checked={fluidSeconds}
              label="Motion"
              onLabel="Fluid"
              offLabel="Tick"
              onChange={setFluidSeconds}
            />
            <Toggle
              checked={showDigitalTime}
              label="Digital time"
              onLabel="On"
              offLabel="Off"
              onChange={setShowDigitalTime}
            />
          </div>

          {error && (
            <p className="font-mono text-[12px] text-red-400">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || saved}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-mono text-[12px] font-bold uppercase tracking-widest transition-all duration-200 ${
              saved
                ? "bg-teal-500/20 border border-teal-400/30 text-teal-300"
                : "bg-luster-500/20 border border-luster-400/30 text-luster-300 hover:bg-luster-500/30 disabled:opacity-50"
            }`}
          >
            {saved ? (
              <><Check className="w-3.5 h-3.5" /> Saved</>
            ) : saving ? (
              "Saving..."
            ) : (
              "Apply"
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
